import { supabase } from '../lib/supabaseClient';
import { RealtimeChannel } from '@supabase/supabase-js';

export interface RealtimeConfig {
  table: string;
  event: 'INSERT' | 'UPDATE' | 'DELETE' | '*';
  schema?: string;
  filter?: string;
  callback: (payload: any) => void;
  immediateUpdate?: boolean; // For instant UI updates
  optimisticUpdate?: boolean; // For optimistic UI updates
}

export interface OptimisticUpdate {
  id: string;
  type: 'INSERT' | 'UPDATE' | 'DELETE';
  data: any;
  timestamp: number;
}

export class EnhancedRealtimeService {
  private static instance: EnhancedRealtimeService;
  private channels: Map<string, RealtimeChannel> = new Map();
  private optimisticUpdates: Map<string, OptimisticUpdate[]> = new Map();
  private updateQueue: Map<string, NodeJS.Timeout> = new Map();
  private connectionStatus: 'connected' | 'connecting' | 'disconnected' = 'disconnected';
  private reconnectAttempts: Map<string, number> = new Map();
  private maxReconnectAttempts = 5;
  private batchUpdates: Map<string, OptimisticUpdate[]> = new Map();
  private batchTimer: Map<string, NodeJS.Timeout> = new Map();

  static getInstance(): EnhancedRealtimeService {
    if (!EnhancedRealtimeService.instance) {
      EnhancedRealtimeService.instance = new EnhancedRealtimeService();
    }
    return EnhancedRealtimeService.instance;
  }

  /**
   * Subscribe to real-time updates with Google Calendar-like instant response
   */
  async subscribeToChannel(config: RealtimeConfig): Promise<RealtimeChannel> {
    const channelName = `enhanced_${config.table}_${Date.now()}`;
    
    console.log(`EnhancedRealtime: Setting up subscription for ${config.table} with channel ${channelName}`);
    
    // Clean up existing channel if it exists
    if (this.channels.has(channelName)) {
      console.log(`EnhancedRealtime: Cleaning up existing channel ${channelName}`);
      this.channels.get(channelName)?.unsubscribe();
      this.channels.delete(channelName);
    }

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes' as any,
        {
          event: config.event,
          schema: config.schema || 'public',
          table: config.table,
          filter: config.filter,
        },
        (payload: any) => {
          console.log(`EnhancedRealtime: ${config.event} event received for ${config.table}:`, payload);
          
          // Immediate optimistic update for instant UI response (no debounce for critical updates)
          if (config.immediateUpdate) {
            this.handleImmediateUpdate(config, payload);
          }
          
          // Call the provided callback immediately with no delay
          try {
            config.callback(payload);
            console.log(`EnhancedRealtime: Callback executed successfully for ${config.table}`);
          } catch (error) {
            console.error(`EnhancedRealtime: Error in callback for ${config.table}:`, error);
          }
          
          // Update connection status
          this.connectionStatus = 'connected';
        }
      )
      .subscribe((status, error) => {
        console.log(`EnhancedRealtime: Channel ${channelName} status:`, status, error);
        
        if (status === 'SUBSCRIBED') {
          console.log(`EnhancedRealtime: Successfully subscribed to ${config.table}`);
          this.connectionStatus = 'connected';
          this.reconnectAttempts.set(channelName, 0);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.error(`EnhancedRealtime: Channel error for ${config.table}:`, error);
          this.connectionStatus = 'disconnected';
          this.handleReconnection(channelName, config);
        } else if (status === 'JOINING' || status === 'SUBSCRIBING') {
          console.log(`EnhancedRealtime: Connecting to ${config.table}...`);
          this.connectionStatus = 'connecting';
        }
      });

    this.channels.set(channelName, channel);
    return channel;
  }

  /**
   * Handle immediate optimistic updates for instant UI response
   */
  private handleImmediateUpdate(config: RealtimeConfig, payload: any): void {
    const updateKey = `${config.table}_${payload.newRecord?.id || payload.oldRecord?.id}`;
    
    const optimisticUpdate: OptimisticUpdate = {
      id: updateKey,
      type: payload.eventType,
      data: payload,
      timestamp: Date.now(),
    };

    // Store optimistic update
    if (!this.optimisticUpdates.has(config.table)) {
      this.optimisticUpdates.set(config.table, []);
    }
    this.optimisticUpdates.get(config.table)!.push(optimisticUpdate);

    // Clear old optimistic updates (keep last 10 for better performance)
    const updates = this.optimisticUpdates.get(config.table)!;
    if (updates.length > 10) {
      this.optimisticUpdates.set(config.table, updates.slice(-10));
    }

    // Immediate dispatch for instant UI response (no debounce for critical updates)
    this.processOptimisticUpdates(config.table);
  }

  /**
   * Process optimistic updates and trigger UI updates immediately
   */
  private processOptimisticUpdates(table: string): void {
    const updates = this.optimisticUpdates.get(table) || [];
    if (updates.length === 0) return;

    // Group updates by type for efficient processing
    const insertUpdates = updates.filter(u => u.type === 'INSERT');
    const updateUpdates = updates.filter(u => u.type === 'UPDATE');
    const deleteUpdates = updates.filter(u => u.type === 'DELETE');

    // Dispatch custom events immediately for instant UI updates
    if (insertUpdates.length > 0) {
      window.dispatchEvent(new CustomEvent('realtime-insert', {
        detail: { table, updates: insertUpdates }
      }));
    }

    if (updateUpdates.length > 0) {
      window.dispatchEvent(new CustomEvent('realtime-update', {
        detail: { table, updates: updateUpdates }
      }));
    }

    if (deleteUpdates.length > 0) {
      window.dispatchEvent(new CustomEvent('realtime-delete', {
        detail: { table, updates: deleteUpdates }
      }));
    }

    // Clear processed updates after a very short delay to allow UI to settle
    setTimeout(() => {
      this.optimisticUpdates.set(table, []);
    }, 50); // Reduced from 100ms to 50ms for faster cleanup
  }

  /**
   * Handle automatic reconnection with exponential backoff
   */
  private handleReconnection(channelName: string, config: RealtimeConfig): void {
    const attempts = this.reconnectAttempts.get(channelName) || 0;
    
    if (attempts < this.maxReconnectAttempts) {
      const delay = Math.min(1000 * Math.pow(2, attempts), 30000);
      
      setTimeout(() => {
        console.log(`EnhancedRealtime: Attempting reconnection for ${channelName} (attempt ${attempts + 1})`);
        this.reconnectAttempts.set(channelName, attempts + 1);
        this.subscribeToChannel(config);
      }, delay);
    } else {
      console.error(`EnhancedRealtime: Max reconnection attempts reached for ${channelName}`);
    }
  }

  /**
   * Optimistic update for instant UI response
   */
  async optimisticUpdate<T>(
    table: string,
    operation: 'INSERT' | 'UPDATE' | 'DELETE',
    data: T,
    callback?: (result: T) => void
  ): Promise<void> {
    // Immediately update UI optimistically
    const optimisticUpdate: OptimisticUpdate = {
      id: `${table}_${Date.now()}`,
      type: operation,
      data: data,
      timestamp: Date.now(),
    };

    // Store for potential rollback
    if (!this.optimisticUpdates.has(table)) {
      this.optimisticUpdates.set(table, []);
    }
    this.optimisticUpdates.get(table)!.push(optimisticUpdate);

    // Trigger immediate UI update
    window.dispatchEvent(new CustomEvent('realtime-optimistic', {
      detail: { table, operation, data }
    }));

    // Call callback immediately for instant response
    if (callback) {
      callback(data);
    }

    // Process the actual database operation
    try {
      let result;
      switch (operation) {
        case 'INSERT':
          result = await supabase.from(table).insert(data);
          break;
        case 'UPDATE':
          result = await supabase.from(table).update(data);
          break;
        case 'DELETE':
          result = await supabase.from(table).delete().eq('id', (data as any).id);
          break;
      }

      if (result.error) {
        // Rollback optimistic update on error
        this.rollbackOptimisticUpdate(table, optimisticUpdate.id);
        throw result.error;
      }
    } catch (error) {
      // Rollback optimistic update on error
      this.rollbackOptimisticUpdate(table, optimisticUpdate.id);
      throw error;
    }
  }

  /**
   * Rollback optimistic update on error
   */
  private rollbackOptimisticUpdate(table: string, updateId: string): void {
    const updates = this.optimisticUpdates.get(table) || [];
    const filteredUpdates = updates.filter(u => u.id !== updateId);
    this.optimisticUpdates.set(table, filteredUpdates);

    // Trigger rollback event
    window.dispatchEvent(new CustomEvent('realtime-rollback', {
      detail: { table, updateId }
    }));
  }

  /**
   * Batch multiple updates for efficiency with immediate UI response
   */
  async batchUpdate<T>(
    table: string,
    updates: Array<{ operation: 'INSERT' | 'UPDATE' | 'DELETE'; data: T }>
  ): Promise<void> {
    // Apply all optimistic updates immediately for instant UI response
    updates.forEach(update => {
      this.optimisticUpdate(table, update.operation, update.data);
    });

    // Process actual database operations in parallel
    const promises = updates.map(async update => {
      try {
        switch (update.operation) {
          case 'INSERT':
            return await supabase.from(table).insert(update.data);
          case 'UPDATE':
            return await supabase.from(table).update(update.data);
          case 'DELETE':
            return await supabase.from(table).delete().eq('id', (update.data as any).id);
        }
      } catch (error) {
        console.error('Batch update error:', error);
        throw error;
      }
    });

    await Promise.all(promises);
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): 'connected' | 'connecting' | 'disconnected' {
    return this.connectionStatus;
  }

  /**
   * Get active channel count
   */
  getActiveChannelCount(): number {
    return this.channels.size;
  }

  /**
   * Cleanup all channels and timers
   */
  cleanup(): void {
    // Clear all channels
    this.channels.forEach(channel => channel.unsubscribe());
    this.channels.clear();

    // Clear all timers
    this.updateQueue.forEach(timer => clearTimeout(timer));
    this.updateQueue.clear();

    // Clear batch timers
    this.batchTimer.forEach(timer => clearTimeout(timer));
    this.batchTimer.clear();

    // Clear optimistic updates
    this.optimisticUpdates.clear();
    this.batchUpdates.clear();

    // Reset connection status
    this.connectionStatus = 'disconnected';
  }
}

// Export singleton instance
export const enhancedRealtimeService = EnhancedRealtimeService.getInstance(); 